/* eslint-disable no-nested-ternary */
/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import PropagateLoader from 'react-spinners/PropagateLoader';
import { SideBySideMagnifier } from 'react-image-magnifiers';
import { useDispatch, useSelector } from 'react-redux';
import findAllSubscribedMessages from '../../API/messagesAPI';
import { DateTimeFormat } from '../../utils/dateTimeFormat';
import { errorHandler } from '../../utils/errorHandler';
import Logout from '../../utils/logout';
import {
  currentMessageAction,
  notificationClickAction,
  notificationViewAction,
} from '../../reducers/notification';
import { decryptData, encryptData } from '../../utils/dataEncryptDecrypt';

function MessageRow({
  item,
  dispatch,
  handleClose,
  readMessages,
  setReadMessages,
}) {
  const history = useHistory();
  const handleClick = () => {
    // Regular expression to detect URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const foundUrl = item?.content.match(urlRegex);

    if (foundUrl) {
      // If URL exists in the message content, open the first detected URL
      window.open(foundUrl[0], '_blank');
    } else if (item?.call) {
      // If item.call exists, handle internal navigation
      localStorage.setItem('selectQuickLink', 'Calls');
      dispatch(notificationClickAction(item?.call.callUID));
      dispatch(currentMessageAction(item?.messageUID));
      dispatch(notificationViewAction(false));
      handleClose();

      // Mark the message as read and update localStorage
      setReadMessages(new Set([...readMessages, item.messageUID]));
      const updatedReadMessages = [...readMessages, item.messageUID];
      const encryptedData = encryptData(updatedReadMessages);
      localStorage.setItem('readMessages', encryptedData);
      history.push('/dashboard');
    } else {
      // If item.call does not exist and no URL is present, redirect to external link
      window.location.href = '/analysis';
    }
  };

  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const cursorStyle = item?.call
    ? { cursor: 'pointer', borderBottom: 'solid 2px #eee' }
    : { borderBottom: 'solid 2px #eee' };

  const title = item?.call ? 'Click Here For More Details' : '';

  return (
    <tr
      onClick={handleClick}
      style={cursorStyle}
      title={title}
      className={item?.call ? 'call' : 'ordinary'}
    >
      <td
        className={`p-3 ${
          readMessages.has(item?.messageUID) ? 'bg-white' : ''
        }`}
      >
        <div className='d-flex align-items-center justify-content-between'>
          <h5 className='fw-bold fs'>
            {item?.call ? (
              <i className='bi bi-star-fill text-warning' />
            ) : (
              <i className='bi bi-info-circle-fill text-secondary' />
            )}{' '}
            {item?.header}
          </h5>
          {isToday(new Date(item?.createdAt)) &&
            !readMessages.has(item?.messageUID) && (
              <i
                className='bi bi-circle-fill text-success blink_me'
                style={{ fontSize: '0.8em' }}
              />
            )}
        </div>

        <p style={{ fontSize: '1.0em' }}>{item?.content}</p>
        {item?.attachment != null && item?.attachment !== '' && (
          <SideBySideMagnifier
            imageSrc={item?.attachment}
            fillAlignTop
            style={{ width: 300 }}
            imageAlt='Screenshot'
            className='mt-3'
          />
        )}
        <p className='float-end font-monospace text-black-50'>
          {DateTimeFormat(item?.createdAt)}
        </p>
      </td>
    </tr>
  );
}

function Notification({ setNotificationPopup, handleNotificationCloseClick }) {
  const mountedRef = useRef(true);
  const dispatch = useDispatch();
  const [notificationLoader, setNotificationLoader] = useState(false);
  const [messages, setMessages] = useState([]);
  const [showMore, setShowMore] = useState(false);
  const [readMessages, setReadMessages] = useState(new Set());

  const userStoreSession = useSelector(
    (state) => state.UserSessionReducer.userSessionStoreData
  );
  const userStoreToken = useSelector(
    (state) => state.UserSessionReducer.userTokenStoreData
  );
  const userStoreSessionId = useSelector(
    (state) => state.UserSessionReducer.userSessionIdStoreData
  );

  const handleClose = () => {
    setNotificationPopup(false);
    handleNotificationCloseClick();
  };
  const handleShowMore = () => setShowMore((prevShowMore) => !prevShowMore);

  const getAllMessages = async () => {
    setNotificationLoader(true);
    try {
      if (userStoreSession != null && userStoreSessionId != null) {
        const userToken = userStoreToken;
        const userSessionId = userStoreSessionId;
        const res = await findAllSubscribedMessages(userToken, userSessionId);
        let allMessages = res.data.calls.flatMap((call) => call.messages);
        allMessages.push(res.data.messages);
        allMessages = allMessages?.flat();
        allMessages.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        if (mountedRef.current) {
          setMessages(allMessages);
          setNotificationLoader(false);
        }
      } else {
        Logout();
      }
    } catch (error) {
      await errorHandler(error);
      if (mountedRef.current) setNotificationLoader(false);
    }
  };

  const lastDate = moment().subtract(3, 'days').format('YYYY-MM-DD');

  const lastThreeDaysMessages = messages?.filter((item) =>
    moment(item?.createdAt)?.isAfter(lastDate, 'day')
  );

  const relevantMessages = showMore ? messages : lastThreeDaysMessages;

  useEffect(() => {
    const encryptedData = localStorage.getItem('readMessages');
    const savedReadMessages = encryptedData ? decryptData(encryptedData) : [];
    setReadMessages(new Set(savedReadMessages));

    getAllMessages();
    const handleScroll = () => setNotificationPopup(false);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      mountedRef.current = false;
    };
  }, []);

  return (
    <div className='bg-light'>
      <div
        className='d-flex p-3 justify-content-between align-items-center d-flex me-1 me-lg-0'
        style={{ borderBottom: 'solid 2px #eee' }}
      >
        <h3 className='fw-bold fs-4'>Notifications</h3>
        <button
          type='button'
          className='btn btn-link text-danger fw-bold'
          onClick={handleClose}
        >
          Close
        </button>
      </div>

      <table className='table table-hover table-borderless'>
        <tbody>
          {relevantMessages?.map((item) => (
            <MessageRow
              key={item?.messageUID}
              item={item}
              dispatch={dispatch}
              handleClose={handleClose}
              readMessages={readMessages}
              setReadMessages={setReadMessages}
            />
          ))}
          {!notificationLoader ? (
            messages.length === 0 ? (
              <tr>
                <td className='text-center'>
                  <p>Notifications are not available at the moment</p>
                </td>
              </tr>
            ) : null
          ) : (
            <tr>
              <td colSpan='some_number'>
                <div className='p-5 text-center'>
                  <PropagateLoader color='#0d94fb' size={10} />
                </div>
              </td>
            </tr>
          )}
          <tr>
            <td className='text-center'>
              <button
                type='button'
                className='btn btn-link'
                onClick={handleShowMore}
              >
                {showMore ? 'Show Less' : 'Show More'}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default Notification;
